// src/pages/HousekeepingChecklistTemplatesPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Drawer,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import housekeepingApi from "../api/housekeepingApi";
import notify from "../utils/notify";

const { Title, Text } = Typography;

const TASK_TYPES = [
  { value: "RoomCleaning", label: "Room Cleaning" },
  { value: "PublicAreaCleaning", label: "Public Area Cleaning" },
  { value: "LaundryService", label: "Laundry Service" },
  // Add more ONLY if they exist in your TaskType enum
];

const norm = (s) =>
  String(s ?? "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");

const getId = (x) => x?.ChecklistTemplateId ?? x?.checklistTemplateId ?? x?.id ?? x?.Id;
const getItems = (t) => t?.Items ?? t?.items ?? [];

const HousekeepingChecklistTemplatesPage = () => {
  const { authData } = useAuth();
  const navigate = useNavigate();

  const hotelId = authData?.hotelId;

  const [loading, setLoading] = useState(true);
  const [roomTypesLoading, setRoomTypesLoading] = useState(true);

  const [roomTypes, setRoomTypes] = useState([]);
  const [hotelTemplates, setHotelTemplates] = useState([]);

  const [query, setQuery] = useState("");
  const [taskTypeFilter, setTaskTypeFilter] = useState("All");

  // preview
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  // create/edit
  const [editOpen, setEditOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [form] = Form.useForm();

  const normalizeRoomTypes = (data) => {
    const arr = Array.isArray(data) ? data : [];
    return arr.map((x) => ({
      roomTypeId: x.roomTypeId ?? x.RoomTypeId ?? x.id ?? x.Id,
      name:
        x.name ??
        x.typeName ??
        x.TypeName ??
        x.roomTypeName ??
        `RoomType #${x.roomTypeId ?? x.id ?? ""}`,
    }));
  };

  const fetchRoomTypes = async () => {
    if (!hotelId) return;
    setRoomTypesLoading(true);
    try {
      const res = await housekeepingApi.getRoomTypes(hotelId);
      setRoomTypes(normalizeRoomTypes(res?.data));
    } catch (e) {
      console.error(e);
      notify.error({
        title: "Failed to load room types",
        description:
          e?.response?.data?.message ||
          "Please add the /api/hotels/{hotelId}/room-types endpoint.",
      });
      setRoomTypes([]);
    } finally {
      setRoomTypesLoading(false);
    }
  };

  const refreshAll = async () => {
    if (!hotelId) {
      notify.error({
        title: "Missing hotelId",
        description: "Please log in again.",
      });
      return;
    }

    setLoading(true);
    try {
      // Per-hotel only (no System/Library)
      const hotelRes = await housekeepingApi.getChecklistTemplatesByHotel(hotelId, false);
      setHotelTemplates(Array.isArray(hotelRes?.data) ? hotelRes.data : []);
    } catch (e) {
      console.error(e);
      notify.error({
        title: "Failed to load templates",
        description: e?.response?.data?.message || "Please try again.",
      });
      setHotelTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoomTypes();
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId]);

  const filtered = useMemo(() => {
    const q = norm(query);
    const tt = taskTypeFilter;

    return (Array.isArray(hotelTemplates) ? hotelTemplates : []).filter((t) => {
      const name = norm(t.Name ?? t.name ?? "");
      const code = norm(t.TemplateCode ?? t.templateCode ?? "");
      const type = String(t.TaskType ?? t.taskType ?? "");
      const itemCount = getItems(t).length;

      const qOk =
        !q ||
        name.includes(q) ||
        code.includes(q) ||
        norm(type).includes(q) ||
        String(itemCount).includes(q);

      const typeOk = tt === "All" || type === tt;
      return qOk && typeOk;
    });
  }, [hotelTemplates, query, taskTypeFilter]);

  const openPreview = (tpl) => {
    setPreviewTemplate(tpl);
    setPreviewOpen(true);
  };

  const openCreate = () => {
    setEditingTemplate(null);
    form.resetFields();
    form.setFieldsValue({
      templateCode: "",
      name: "",
      taskType: "RoomCleaning",
      appliesTo: "Room",
      isActive: true,
      // Room-type scope (stored for now in AppliesTo string if you keep backend as-is)
      appliesToAllRoomTypes: true,
      roomTypeIds: [],
      items: [
        { category: "Entry & Safety", description: "Knock/announce 'Housekeeping' and enter safely", isRequired: true, isActive: true },
        { category: "Bed & Linen", description: "Make bed to standard (or change linen if required)", isRequired: true, isActive: true },
        { category: "Bathroom", description: "Clean bathroom fixtures and restock toiletries", isRequired: true, isActive: true },
        { category: "Floors", description: "Vacuum/mop floors including edges", isRequired: true, isActive: true },
        { category: "Final", description: "Final inspection and set room to Ready / Needs Maintenance", isRequired: true, isActive: true },
      ],
    });
    setEditOpen(true);
  };

  const openEdit = (tpl) => {
    setEditingTemplate(tpl);

    const items = getItems(tpl).map((x) => ({
      checklistTemplateItemId: x.ChecklistTemplateItemId ?? x.checklistTemplateItemId ?? x.id ?? x.Id,
      category: x.Category ?? x.category ?? "",
      description: x.Description ?? x.description ?? "",
      sortOrder: x.SortOrder ?? x.sortOrder ?? 0,
      isRequired: x.IsRequired ?? x.isRequired ?? true,
      isActive: x.IsActive ?? x.isActive ?? true,
    }));

    form.resetFields();
    form.setFieldsValue({
      templateCode: tpl.TemplateCode ?? tpl.templateCode ?? "",
      name: tpl.Name ?? tpl.name ?? "",
      taskType: tpl.TaskType ?? tpl.taskType ?? "RoomCleaning",
      appliesTo: tpl.AppliesTo ?? tpl.appliesTo ?? "Room",
      isActive: tpl.IsActive ?? tpl.isActive ?? true,

      // These two fields only truly work if you extend backend model/DTO.
      appliesToAllRoomTypes: true,
      roomTypeIds: [],

      items: items.length
        ? items.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        : [{ description: "", isActive: true, isRequired: true }],
    });

    setEditOpen(true);
  };

  const handleSaveTemplate = async () => {
    if (!hotelId) return;

    try {
      const values = await form.validateFields();

      // DTO matches CreateChecklistTemplateDto:
      // TemplateCode, Name, TaskType, AppliesTo, Items[]
      const dto = {
        templateCode: values.templateCode?.trim() || null,
        name: values.name?.trim(),
        taskType: values.taskType,
        appliesTo: values.appliesTo?.trim() || null,
        items: (values.items || [])
          .map((x, idx) => ({
            category: (x.category || "").trim() || null,
            description: String(x.description || "").trim(),
            sortOrder: (idx + 1) * 10,
            isRequired: x.isRequired !== false,
            isActive: x.isActive !== false,
          }))
          .filter((x) => x.description.length > 0),
      };

      if (!dto.name) {
        notify.warning({ title: "Template name is required." });
        return;
      }

      if (dto.items.length === 0) {
        notify.warning({ title: "Add at least 1 checklist item." });
        return;
      }

      if (editingTemplate) {
        const templateId = getId(editingTemplate);
        await housekeepingApi.updateChecklistTemplate(hotelId, templateId, dto);
        notify.success({ title: "Template updated" });
      } else {
        await housekeepingApi.createChecklistTemplate(hotelId, dto);
        notify.success({ title: "Template created" });
      }

      setEditOpen(false);
      setEditingTemplate(null);
      await refreshAll();
    } catch (e) {
      if (e?.errorFields) return;
      console.error(e);
      notify.error({
        title: "Save failed",
        description: e?.response?.data?.message || "Please try again.",
      });
    }
  };

  const handleDisableTemplate = async (tpl) => {
    if (!hotelId) return;
    try {
      const templateId = getId(tpl);
      await housekeepingApi.disableChecklistTemplate(hotelId, templateId);
      notify.success({ title: "Template disabled" });
      await refreshAll();
    } catch (e) {
      console.error(e);
      notify.error({
        title: "Disable failed",
        description: e?.response?.data?.message || "Please try again.",
      });
    }
  };

  const columns = [
    {
      title: "Template",
      dataIndex: "Name",
      render: (_, r) => {
        const name = r.Name ?? r.name ?? "Untitled";
        const code = r.TemplateCode ?? r.templateCode ?? "";
        const taskType = r.TaskType ?? r.taskType ?? "-";
        const active = r.IsActive ?? r.isActive ?? true;

        return (
          <Space direction="vertical" size={0}>
            <Space wrap>
              <Text strong>{name}</Text>
              {!active && <Tag color="red">Disabled</Tag>}
              <Tag color="blue">{taskType}</Tag>
              {code && <Tag>{code}</Tag>}
            </Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Items: {getItems(r).length}
            </Text>
          </Space>
        );
      },
    },
    {
      title: "Applies To",
      key: "scope",
      width: 240,
      render: (_, r) => <Tag color="default">{r.AppliesTo ?? r.appliesTo ?? "—"}</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 240,
      render: (_, r) => {
        const active = r.IsActive ?? r.isActive ?? true;

        return (
          <Space>
            <Tooltip title="Preview items">
              <Button icon={<EyeOutlined />} onClick={() => openPreview(r)} />
            </Tooltip>

            <Tooltip title="Edit template">
              <Button icon={<EditOutlined />} onClick={() => openEdit(r)} disabled={!active} />
            </Tooltip>

            <Tooltip title="Disable template">
              <Button danger icon={<DeleteOutlined />} onClick={() => handleDisableTemplate(r)} disabled={!active} />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        style={{ marginBottom: 16 }}
        title={
          <Space>
            <SettingOutlined />
            <Text strong>Housekeeping Checklist Templates</Text>
            <Badge count={hotelTemplates.length} showZero />
          </Space>
        }
        extra={
          <Space wrap>
            <Input
              allowClear
              placeholder="Search templates..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ width: 260 }}
            />

            <Select
              value={taskTypeFilter}
              onChange={setTaskTypeFilter}
              options={[{ value: "All", label: "All Task Types" }, ...TASK_TYPES]}
              style={{ width: 220 }}
            />

            <Button icon={<ReloadOutlined />} onClick={refreshAll} loading={loading}>
              Refresh
            </Button>

            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Create Template
            </Button>

            <Button onClick={() => navigate("/housekeeping")} type="default">
              Back to Housekeeping
            </Button>
          </Space>
        }
      >
        <Row gutter={[16, 16]}>
          <Col span={24}>
            {loading ? (
              <div style={{ padding: 36, textAlign: "center" }}>
                <Text type="secondary">Loading templates...</Text>
              </div>
            ) : filtered.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No templates found for this hotel"
              />
            ) : (
              <Table
                rowKey={(r) => String(getId(r))}
                columns={columns}
                dataSource={filtered}
                pagination={{ pageSize: 10, showSizeChanger: true }}
                scroll={{ x: true }}
              />
            )}
          </Col>
        </Row>
      </Card>

      {/* PREVIEW DRAWER */}
      <Drawer
        title="Template Preview"
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        size="default"
        styles={{ wrapper: { width: 560 } }} // avoids deprecated "width"
      >
        {!previewTemplate ? (
          <Empty />
        ) : (
          <>
            <Title level={5} style={{ marginTop: 0 }}>
              {previewTemplate.Name ?? previewTemplate.name ?? "Untitled"}
            </Title>

            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="Task Type">
                {previewTemplate.TaskType ?? previewTemplate.taskType ?? "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Applies To">
                {previewTemplate.AppliesTo ?? previewTemplate.appliesTo ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {(previewTemplate.IsActive ?? previewTemplate.isActive ?? true) ? (
                  <Tag color="green">Active</Tag>
                ) : (
                  <Tag color="red">Disabled</Tag>
                )}
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <Title level={5}>Checklist Items</Title>
            <div style={{ display: "grid", gap: 8 }}>
              {getItems(previewTemplate).length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No items" />
              ) : (
                getItems(previewTemplate)
                  .slice()
                  .sort(
                    (a, b) =>
                      (a.SortOrder ?? a.sortOrder ?? 0) -
                      (b.SortOrder ?? b.sortOrder ?? 0)
                  )
                  .map((it, idx) => (
                    <Card
                      key={
                        it.ChecklistTemplateItemId ??
                        it.checklistTemplateItemId ??
                        it.id ??
                        idx
                      }
                      size="small"
                    >
                      <Space>
                        <Tag>{idx + 1}</Tag>
                        {(it.Category ?? it.category) && (
                          <Tag color="default">{it.Category ?? it.category}</Tag>
                        )}
                        <Text>{it.Description ?? it.description}</Text>
                        {(it.IsRequired ?? it.isRequired) === false && (
                          <Tag color="gold">Optional</Tag>
                        )}
                        {(it.IsActive ?? it.isActive) === false && (
                          <Tag color="red">Disabled</Tag>
                        )}
                      </Space>
                    </Card>
                  ))
              )}
            </div>
          </>
        )}
      </Drawer>

      {/* CREATE / EDIT MODAL */}
      <Modal
        title={editingTemplate ? "Edit Checklist Template" : "Create Checklist Template"}
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={handleSaveTemplate}
        okText={editingTemplate ? "Save" : "Create"}
        width={980}
        destroyOnHidden // avoids deprecated destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="templateCode" label="Template Code (optional)">
                <Input allowClear placeholder="e.g., ROOM_CLEAN_STANDARD" />
              </Form.Item>
            </Col>

            <Col xs={24} md={10}>
              <Form.Item
                name="name"
                label="Template Name"
                rules={[
                  { required: true, message: "Please enter a name" },
                  { max: 120, message: "Max 120 characters" },
                ]}
              >
                <Input allowClear placeholder="e.g., Room Turnover – Standard" />
              </Form.Item>
            </Col>

            <Col xs={24} md={6}>
              <Form.Item name="taskType" label="Task Type" rules={[{ required: true }]}>
                <Select options={TASK_TYPES} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={10}>
              <Form.Item name="appliesTo" label="Applies To (category)">
                <Input allowClear placeholder="Room / PublicArea / Laundry ..." />
              </Form.Item>
            </Col>

            <Col xs={24} md={6}>
              <Form.Item name="isActive" label="Active" valuePropName="checked">
                <Switch defaultChecked />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                label="Room Types (future enhancement)"
                help="Your backend model currently stores AppliesTo only. If you want true room-type scoping, we should extend ChecklistTemplate."
              >
                <Select
                  mode="multiple"
                  allowClear
                  disabled
                  loading={roomTypesLoading}
                  options={roomTypes.map((rt) => ({ value: rt.roomTypeId, label: rt.name }))}
                  placeholder="Enable after backend extension"
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider />
          <Title level={5}>Checklist Items</Title>

          <Form.List name="items">
            {(fields, { add, remove, move }) => (
              <>
                {fields.length === 0 && (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Add at least one item" />
                )}

                {fields.map((field, idx) => (
                  <Card
                    key={field.key}
                    size="small"
                    style={{ marginBottom: 10 }}
                    title={
                      <Space>
                        <Tag>{idx + 1}</Tag>
                        <Text strong>Item</Text>
                      </Space>
                    }
                    extra={
                      <Space>
                        <Button size="small" disabled={idx === 0} onClick={() => move(idx, idx - 1)}>
                          Up
                        </Button>
                        <Button
                          size="small"
                          disabled={idx === fields.length - 1}
                          onClick={() => move(idx, idx + 1)}
                        >
                          Down
                        </Button>
                        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                      </Space>
                    }
                  >
                    <Row gutter={12}>
                      <Col xs={24} md={6}>
                        <Form.Item {...field} name={[field.name, "category"]} label="Category">
                          <Input placeholder="Bathroom / Floors / Linen ..." />
                        </Form.Item>
                      </Col>

                      <Col xs={24} md={12}>
                        <Form.Item
                          {...field}
                          name={[field.name, "description"]}
                          label="Description"
                          rules={[
                            { required: true, message: "Enter description" },
                            { max: 300, message: "Max 300 characters" },
                          ]}
                        >
                          <Input placeholder="e.g., Replace towels and restock toiletries" />
                        </Form.Item>
                      </Col>

                      <Col xs={12} md={3}>
                        <Form.Item
                          name={[field.name, "isRequired"]}
                          label="Required"
                          valuePropName="checked"
                          initialValue={true}
                        >
                          <Switch />
                        </Form.Item>
                      </Col>

                      <Col xs={12} md={3}>
                        <Form.Item
                          name={[field.name, "isActive"]}
                          label="Active"
                          valuePropName="checked"
                          initialValue={true}
                        >
                          <Switch />
                        </Form.Item>
                      </Col>

                      <Form.Item name={[field.name, "checklistTemplateItemId"]} hidden>
                        <Input />
                      </Form.Item>
                    </Row>
                  </Card>
                ))}

                <Button
                  type="dashed"
                  block
                  icon={<PlusOutlined />}
                  onClick={() => add({ category: "", description: "", isRequired: true, isActive: true })}
                >
                  Add Item
                </Button>
              </>
            )}
          </Form.List>

          <Divider />
          <Text type="secondary">These templates are instantiated into task checklist items when tasks are created.</Text>
        </Form>
      </Modal>
    </div>
  );
};

export default HousekeepingChecklistTemplatesPage;
